import React, { useState } from "react";
import DataTable from "react-data-table-component";
import { CSVLink } from "react-csv";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import Swal from "sweetalert2";
import "bootstrap/dist/css/bootstrap.min.css";
import "remixicon/fonts/remixicon.css";

const InquiryList = ({ inquiries = [], onEdit, onDelete, onAddInquiry, onEnroll }) => {
    const [filterText, setFilterText] = useState("");
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);

    // ✅ Normalize each inquiry to flatten structure for the table
    const normalizedInquiries = inquiries.map((inq) => ({
        id: inq.master?.id,
        client_name: inq.clientInformation?.client_name || "N/A",
        diagnosis: inq.clientInformation?.diagnosis || "N/A",

        created_at: inq.master?.created_at
            ? new Date(inq.master.created_at).toLocaleDateString()
            : "N/A",

        created_by: inq.master?.creator?.name || "N/A",

        updated_by: inq.master?.updated_by?.name || "N/A",

        updated_at: inq.master?.updated_at
            ? new Date(inq.master.updated_at).toLocaleDateString()
            : "N/A",
    }));


    // ✅ Filter by client name or diagnosis
    const filteredInquiries = normalizedInquiries.filter((inq) => {
        const client = `${inq.client_name}`.toLowerCase();
        const diagnosis = `${inq.diagnosis}`.toLowerCase();
        const search = filterText.toLowerCase();
        return client.includes(search) || diagnosis.includes(search);
    });

    const totalRows = filteredInquiries.length;
    const start = (currentPage - 1) * rowsPerPage + 1;
    const end = Math.min(currentPage * rowsPerPage, totalRows);

    // ✅ Custom Pagination
    const CustomPagination = ({
        onChangePage,
        onChangeRowsPerPage,
        currentPage,
        rowsPerPage,
        rowCount,
    }) => {
        const totalPages = Math.ceil(rowCount / rowsPerPage);

        return (
            <div className="d-flex justify-content-between align-items-center p-3 border-top">
                <div className="d-flex align-items-center gap-2">
                    <span className="text-muted" style={{ fontSize: "0.875rem" }}>
                        Rows per page:
                    </span>
                    <select
                        className="form-select form-select-sm"
                        style={{ width: "80px", fontSize: "0.875rem" }}
                        value={rowsPerPage}
                        onChange={(e) =>
                            onChangeRowsPerPage(Number(e.target.value), currentPage)
                        }
                    >
                        {[5, 10, 15, 20, 25].map((option) => (
                            <option key={option} value={option}>
                                {option}
                            </option>
                        ))}
                    </select>
                </div>

                <div className="d-flex align-items-center gap-1">
                    <button
                        className="btn btn-sm border"
                        style={{
                            borderRadius: "6px",
                            height: "32px",
                            width: "32px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "#495057",
                            border: "1px solid #dee2e6",
                            backgroundColor: "#fff",
                        }}
                        onClick={() => onChangePage(currentPage - 1, rowCount)}
                        disabled={currentPage === 1}
                    >
                        <i className="ri-arrow-left-s-line"></i>
                    </button>

                    <span className="mx-3 text-muted" style={{ fontSize: "0.875rem" }}>
                        Page {currentPage} of {totalPages}
                    </span>

                    <button
                        className="btn btn-sm border"
                        style={{
                            borderRadius: "6px",
                            height: "32px",
                            width: "32px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "#495057",
                            border: "1px solid #dee2e6",
                            backgroundColor: "#fff",
                        }}
                        onClick={() => onChangePage(currentPage + 1, rowCount)}
                        disabled={currentPage === totalPages}
                    >
                        <i className="ri-arrow-right-s-line"></i>
                    </button>
                </div>
            </div>
        );
    };

    // ✅ Columns
    const columns = [
        {
            name: "S.No",
            cell: (row, index) => index + 1 + (currentPage - 1) * rowsPerPage,
            width: "80px",
        },
        {
            name: "Client Name",
            selector: (row) => row.client_name,
            sortable: true,
        },
        {
            name: "Diagnosis",
            selector: (row) => row.diagnosis,
            sortable: true,
        },
        {
            name: "Created Date",
            selector: (row) => row.created_at,
            sortable: true,
        },
        {
            name: "Created By",
            selector: (row) => row.created_by,
            sortable: true,
        },
        {
            name: "Updated By",
            selector: (row) => row.updated_by,
            sortable: true,
        },
        {
            name: "Updated Date",
            selector: (row) => row.updated_at,
            sortable: true,
        },
        {
            name: "Actions",
            cell: (row) => (
                <div className="d-flex justify-content-center gap-1">
                    {onEnroll && (
                        <button
                            className="btn btn-sm btn-outline-success"
                            title="Enroll as Patient"
                            onClick={() => onEnroll(row.id)}
                        >
                            <i className="ri-user-add-line"></i>
                        </button>
                    )}
                    {onEdit && (
                        <button
                            className="btn btn-sm btn-outline-primary"
                            title="Edit Inquiry"
                            onClick={() => onEdit(row.id)}
                        >
                            <i className="ri-edit-line"></i>
                        </button>
                    )}
                    {onDelete && (
                        <button
                            className="btn btn-sm btn-outline-danger"
                            title="Delete Inquiry"
                            onClick={() => onDelete(row.id)}
                        >
                            <i className="ri-delete-bin-line"></i>
                        </button>
                    )}
                </div>
            ),
            ignoreRowClick: true,
            allowOverflow: true,
            button: true,
            width: "200px",
        },
    ];

    // ✅ Export to PDF
    const handleExportPDF = () => {
        const doc = new jsPDF();
        doc.text("Inquiries List", 14, 10);
        const tableColumn = [
            "S.No",
            "Client Name",
            "Diagnosis",
            "Created Date",
            "Created By",
            "Updated By",
            "Updated Date",
        ];
        const tableRows = [];

        filteredInquiries.forEach((inq, index) => {
            tableRows.push([
                index + 1,
                inq.client_name || "",
                inq.diagnosis || "",
                inq.created_at || "",
                inq.created_by || "",
                inq.updated_by || "",
                inq.updated_at || "",
            ]);
        });

        autoTable(doc, {
            head: [tableColumn],
            body: tableRows,
            startY: 20,
        });

        doc.save("inquiries_list.pdf");
    };

    const handleCopy = () => {
        const copyText = filteredInquiries
            .map(
                (inq, index) =>
                    `${index + 1}\t${inq.client_name}\t${inq.diagnosis}\t${inq.created_at}\t${inq.created_by}\t${inq.updated_by}\t${inq.updated_at}`
            )
            .join("\n");

        navigator.clipboard.writeText(copyText);

        Swal.fire({
            title: "Copied!",
            text: "Inquiry data copied to clipboard.",
            icon: "success",
            timer: 1500,
            showConfirmButton: false,
            position: "top",
            width: "280px",
        });
    };

    const handlePrint = () => {
        const printContent = `
    <html>
    <head>
        <title>Inquiries List</title>
        <style>
            body { font-family: Arial; margin: 20px; }
            table { width: 100%; border-collapse: collapse; }
            th, td { border: 1px solid #ccc; padding: 6px; text-align: left; }
            th { background: #f8f9fa; }
        </style>
    </head>
    <body>
        <h3>Inquiries List</h3>
        <table>
            <thead>
                <tr>
                    <th>S.No</th>
                    <th>Client Name</th>
                    <th>Diagnosis</th>
                    <th>Created Date</th>
                    <th>Created By</th>
                    <th>Updated By</th>
                    <th>Updated Date</th>
                </tr>
            </thead>
            <tbody>
                ${filteredInquiries
                .map(
                    (inq, index) => `
                        <tr>
                            <td>${index + 1}</td>
                            <td>${inq.client_name}</td>
                            <td>${inq.diagnosis}</td>
                            <td>${inq.created_at}</td>
                            <td>${inq.created_by}</td>
                            <td>${inq.updated_by}</td>
                            <td>${inq.updated_at}</td>
                        </tr>`
                )
                .join("")}
            </tbody>
        </table>
    </body>
    </html>
    `;

        const printWindow = window.open("", "_blank");
        printWindow.document.write(printContent);
        printWindow.document.close();
        printWindow.print();
    };

    return (
        <div>
            <div className="card-header d-flex flex-wrap justify-content-between align-items-center gap-2">
                {/* Left: Export + Search */}
                <div className="d-flex align-items-center gap-2 position-relative">
                    <div className="dropdown">
                        <button
                            className="btn btn-label-primary btn-sm dropdown-toggle"
                            data-bs-toggle="dropdown"
                            aria-expanded="false"
                        >
                            <i className="ri-download-2-line me-1"></i> Export
                        </button>
                        <ul className="dropdown-menu shadow-sm">
                            <li>
                                <button className="dropdown-item" onClick={handleExportPDF}>
                                    <i className="ri-file-pdf-line me-1 text-danger"></i> PDF
                                </button>
                            </li>
                            <li>
                                <CSVLink
                                    data={filteredInquiries}
                                    filename="inquiries_export.csv"
                                    className="dropdown-item"
                                >
                                    <i className="ri-file-excel-line me-1 text-success"></i> CSV
                                </CSVLink>
                            </li>
                            <li>
                                <button className="dropdown-item" onClick={handleCopy}>
                                    <i className="ri-file-copy-line me-1"></i> Copy
                                </button>
                            </li>
                            <li>
                                <button className="dropdown-item" onClick={handlePrint}>
                                    <i className="ri-printer-line me-1"></i> Print
                                </button>
                            </li>
                        </ul>

                    </div>

                    <input
                        type="text"
                        placeholder="Search inquiry..."
                        className="form-control form-control-sm"
                        style={{ width: "200px" }}
                        value={filterText}
                        onChange={(e) => setFilterText(e.target.value)}
                    />
                </div>

                {/* Center Info */}
                <h6 className="mb-0 text-center text-muted flex-grow-1">
                    Showing {start}-{end} of {totalRows} inquiries
                </h6>

                {/* Right: Add Inquiry */}
                <div>
                    {onAddInquiry && (
                        <button
                            className="btn btn-sm btn-primary"
                            title="Add Inquiry"
                            onClick={onAddInquiry}
                        >
                            <i className="ri-add-line me-1"></i>
                            Add Inquiry
                        </button>
                    )}
                </div>
            </div>

            {/* Table */}
            <div className="card-body p-2">
                <DataTable
                    columns={columns}
                    data={filteredInquiries}
                    pagination
                    paginationPerPage={rowsPerPage}
                    paginationTotalRows={totalRows}
                    onChangeRowsPerPage={setRowsPerPage}
                    onChangePage={setCurrentPage}
                    paginationComponent={CustomPagination}
                    highlightOnHover
                    striped
                    responsive
                    noDataComponent={<div className="p-3 text-muted">No inquiries found</div>}
                />
            </div>
        </div>
    );
};

export default InquiryList;
